import {
  Field,
  ID,
  InputAndObjectType,
  registerEnumType,
} from '@warebee/shared/util-backend-only-types';
import { isEmpty } from 'lodash';
import {
  LocationFilterType,
  LocationFilterUnion,
} from '../location-filter.model';
import {
  SimulationItemFilterType,
  SimulationItemFilterUnion,
} from '../simulation-item-filter.model';

const ProximityPolicyRuleSubcategorySpecTypeValues = {
  ...SimulationItemFilterType,
  ...LocationFilterType,
};
export type ProximityPolicyRuleSubcategorySpecType =
  typeof ProximityPolicyRuleSubcategorySpecTypeValues;

registerEnumType(ProximityPolicyRuleSubcategorySpecTypeValues, {
  name: 'ProximityPolicyRuleSubcategorySpecType',
});

@InputAndObjectType()
export class ProximityPolicyRuleSubcategorySpec {
  /**
   * @TJS-type string
   */
  @Field(() => ProximityPolicyRuleSubcategorySpecTypeValues)
  type: ProximityPolicyRuleSubcategorySpecType;
}

export enum ProximityPolicyConstraintType {
  NO_CLOSER_THAN = 'NO_CLOSER_THAN',
}

registerEnumType(ProximityPolicyConstraintType, {
  name: 'ProximityPolicyConstraintType',
});

@InputAndObjectType()
export class ProximityPolicyDistanceConstraint {
  @Field()
  distance: number;

  @Field({ nullable: true })
  includeHeightFromFloor?: boolean;
}

@InputAndObjectType()
export class ProximityPolicyRuleCategoryFilter {
  @Field(() => SimulationItemFilterUnion, { nullable: true })
  productsMatch?: SimulationItemFilterUnion;

  @Field(() => LocationFilterUnion, { nullable: true })
  locationsMatch?: LocationFilterUnion;
}

@InputAndObjectType()
export class ProximityPolicyRuleSubcategories {
  @Field(() => [ProximityPolicyRuleSubcategorySpec], { nullable: true })
  groupBy?: ProximityPolicyRuleSubcategorySpec[];

  @Field(() => [ProximityPolicyRuleSubcategorySpec])
  breakBy: ProximityPolicyRuleSubcategorySpec[];
}

@InputAndObjectType()
export class ProximityPolicyRuleConstraint {
  @Field(() => ProximityPolicyConstraintType)
  constraintType: ProximityPolicyConstraintType;

  @Field(() => ProximityPolicyDistanceConstraint, { nullable: true })
  distance?: ProximityPolicyDistanceConstraint;
}

export enum ProximityPolicyRuleType {
  BETWEEN_CATEGORIES = 'BETWEEN_CATEGORIES',
  BETWEEN_SUBCATEGORIES = 'BETWEEN_SUBCATEGORIES',
}

registerEnumType(ProximityPolicyRuleType, {
  name: 'ProximityPolicyRuleType',
});

@InputAndObjectType()
export class ProximityPolicyRule {
  @Field(() => ID, { nullable: true })
  id?: string;

  @Field(() => ProximityPolicyRuleType, {
    nullable: true,
    defaultValue: ProximityPolicyRuleType.BETWEEN_CATEGORIES,
  })
  type?: ProximityPolicyRuleType;

  @Field({ nullable: true })
  title?: string;

  @Field(() => ProximityPolicyRuleCategoryFilter)
  mainCategory: ProximityPolicyRuleCategoryFilter;

  @Field(() => ProximityPolicyRuleCategoryFilter, { nullable: true })
  secondCategory?: ProximityPolicyRuleCategoryFilter;

  @Field(() => ProximityPolicyRuleSubcategories, { nullable: true })
  subcategories?: ProximityPolicyRuleSubcategories;

  @Field(() => [ProximityPolicyRuleConstraint])
  constraint: ProximityPolicyRuleConstraint[];
}

@InputAndObjectType()
export class ProximityPolicy {
  @Field(() => [ProximityPolicyRule])
  rules: ProximityPolicyRule[];
}

export function validateProximityPolicy(policy: ProximityPolicy) {
  for (const r of policy.rules) {
    const effectiveType = r.type || ProximityPolicyRuleType.BETWEEN_CATEGORIES;
    if (effectiveType == ProximityPolicyRuleType.BETWEEN_CATEGORIES) {
      if (!r.secondCategory) {
        throw new Error('second category is required');
      }
    } else if (effectiveType == ProximityPolicyRuleType.BETWEEN_SUBCATEGORIES) {
      if (!r.subcategories) {
        throw new Error('subcategories are required');
      }

      if (isEmpty(r.subcategories.breakBy)) {
        throw new Error('break by specification must not be empty');
      }
    } else {
      throw new Error(`unknown rule type ${effectiveType}`);
    }
  }
}
